import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactFlow, {
    ReactFlowProvider,
    addEdge,
    updateEdge,
    useNodesState,
    useEdgesState,
    Controls,
    Background,
} from 'react-flow-renderer';
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import { i18n } from "../../translate/i18n";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import { toast } from "react-toastify";
import ChatbotService from "../../services/ChatbotService";

import Sidebar from "./components/Sidebar";
import PropertiesPanel from "./components/PropertiesPanel";
import { TextNode, ButtonNode, ListNode, ImageNode, InputNode, ResolveNode, SpecialtiesNode, ServicesNode, ProfessionalsNode, DateSelectionNode, HoursSelectionNode, ConfirmationNode, VideoNode, PixPaymentNode, TriggerConditionNode, TagBlockNode, ChatGPTNode, ReturnToChatGPTNode, PerplexityNode, ReturnToPerplexityNode, CarouselNode, StartOrderNode, PriceNode, OrderAddressNode, OrderSummaryNode, JumpNode, ProductsNode, QueueSelectionNode, ButtonEdge } from "./components/CustomNodes";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ConfirmationModal from "../../components/ConfirmationModal";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import GetAppIcon from "@material-ui/icons/GetApp";
import PublishIcon from "@material-ui/icons/Publish";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    editorPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        height: "calc(100vh - 100px)",
        display: "flex",
        flexDirection: "column",
    },
    body: {
        display: "flex",
        flex: 1,
        height: "100%"
    },
    flowContainer: {
        flex: 1,
        border: "1px solid #ddd",
        borderRadius: 4,
        position: "relative"
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 10,
        gap: 10
    }
}));

const nodeTypes = {
    text: TextNode,
    button: ButtonNode,
    list: ListNode,
    image: ImageNode,
    video: VideoNode,
    input: InputNode,
    resolve: ResolveNode,
    specialties: SpecialtiesNode,
    services: ServicesNode,
    professionals: ProfessionalsNode,
    date_selection: DateSelectionNode,
    hours_selection: HoursSelectionNode,
    confirmation: ConfirmationNode,
    pix_payment: PixPaymentNode,
    trigger_condition: TriggerConditionNode,
    tag_block: TagBlockNode,
    chatgpt: ChatGPTNode,
    return_to_chatgpt: ReturnToChatGPTNode,
    perplexity: PerplexityNode,
    return_to_perplexity: ReturnToPerplexityNode,
    carousel: CarouselNode,
    start_order: StartOrderNode,
    price: PriceNode,
    order_address: OrderAddressNode,
    order_summary: OrderSummaryNode,
    jump: JumpNode,
    products: ProductsNode,
    queue_selection: QueueSelectionNode
};

const edgeTypes = {
    buttonEdge: ButtonEdge,
};

const initialNodes = [
    {
        id: '1',
        type: 'text',
        data: { label: 'Welcome Message' },
        position: { x: 250, y: 5 },
    },
];

const Chatbot = () => {
    const classes = useStyles();
    const [count, setCount] = useState(0);
    const edgeUpdateSuccessful = useRef(true);
    const [view, setView] = useState("LIST"); // LIST or EDITOR
    const [chatbots, setChatbots] = useState([]);
    const [loading, setLoading] = useState(false);

    // Editor State
    const reactFlowWrapper = useRef(null);
    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const [chatbotId, setChatbotId] = useState(null);
    const [trigger, setTrigger] = useState("");
    const [name, setName] = useState("My Chatbot");
    const [selectedNode, setSelectedNode] = useState(null);

    // Confirmation Modal State
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deletingChatbot, setDeletingChatbot] = useState(null);

    useEffect(() => {
        console.log("[Chatbot Editor] Initialized. Registered node types:", Object.keys(nodeTypes));
        loadChatbots();
    }, []);

    const loadChatbots = async () => {
        setLoading(true);
        try {
            const data = await ChatbotService.list();
            setChatbots(data);
        } catch (err) {
            console.error(err);
            toast.error("Error loading chatbots");
        }
        setLoading(false);
    };

    const handleCreateChatbot = () => {
        setChatbotId(null);
        setName("New Chatbot");
        setTrigger("");
        setNodes(initialNodes);
        setEdges([]);
        setSelectedNode(null);
        setView("EDITOR");
    };

    const handleEditChatbot = (bot) => {
        setChatbotId(bot.id);
        setName(bot.name);
        setTrigger(bot.trigger);

        if (bot.flow) {
            const flow = typeof bot.flow === 'string' ? JSON.parse(bot.flow) : bot.flow;
            setNodes(flow.nodes || []);
            setEdges(flow.edges || []);
        } else {
            setNodes(initialNodes);
            setEdges([]);
        }
        setSelectedNode(null);
        setView("EDITOR");
    };

    const handleDeleteChatbot = async (id) => {
        try {
            await ChatbotService.delete(id);
            toast.success("Chatbot deleted!");
            setConfirmModalOpen(false);
            setDeletingChatbot(null);
            loadChatbots();
        } catch (err) {
            console.error(err);
            toast.error("Error deleting chatbot");
        }
    };

    const onConnect = useCallback((params) => {
        setEdges((eds) => addEdge({ ...params, type: 'buttonEdge' }, eds));
    }, [setEdges]);

    const onEdgeUpdateStart = useCallback(() => {
        edgeUpdateSuccessful.current = false;
    }, []);

    const onEdgeUpdate = useCallback((oldEdge, newConnection) => {
        edgeUpdateSuccessful.current = true;
        setEdges((els) => updateEdge(oldEdge, newConnection, els));
    }, []);

    const onEdgeUpdateEnd = useCallback((_, edge) => {
        if (!edgeUpdateSuccessful.current) {
            setEdges((eds) => eds.filter((e) => e.id !== edge.id));
        }
        edgeUpdateSuccessful.current = true;
    }, []);

    const onEdgeDelete = useCallback((edgeId) => {
        setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    }, [setEdges]);

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();

            const type = event.dataTransfer.getData('application/reactflow');

            if (typeof type === 'undefined' || !type) {
                return;
            }

            const position = reactFlowInstance.project({
                x: event.clientX - reactFlowWrapper.current.getBoundingClientRect().left,
                y: event.clientY - reactFlowWrapper.current.getBoundingClientRect().top,
            });
            const newNode = {
                id: new Date().getTime().toString(),
                type,
                position,
                data: { label: `${type} node` },
            };

            setNodes((nds) => nds.concat(newNode));
        },
        [reactFlowInstance]
    );

    const onNodeClick = (event, node) => {
        setSelectedNode(node);
    };

    const onDuplicateNode = useCallback((nodeId) => {
        setNodes((nds) => {
            const nodeToDuplicate = nds.find(n => n.id === nodeId);
            if (!nodeToDuplicate) return nds;

            const newNode = {
                ...nodeToDuplicate,
                id: new Date().getTime().toString(),
                position: {
                    x: nodeToDuplicate.position.x + 50,
                    y: nodeToDuplicate.position.y + 50,
                },
                data: { ...nodeToDuplicate.data }
            };

            return nds.concat(newNode);
        });
        toast.success("Bloco clonado com sucesso!");
    }, [setNodes]);

    const onSave = useCallback(async () => {
        if (reactFlowInstance) {
            const flow = reactFlowInstance.toObject();

            const chatbotData = {
                name,
                trigger,
                flow
            };

            try {
                if (chatbotId) {
                    await ChatbotService.update(chatbotId, chatbotData);
                    toast.success("Chatbot atualizado com sucesso!");
                } else {
                    const response = await ChatbotService.create(chatbotData);
                    setChatbotId(response.id);
                    toast.success("Chatbot criado com sucesso!");
                }
                loadChatbots();
            } catch (err) {
                console.error(err);
                toast.error("Erro ao salvar chatbot");
            }
        }
    }, [reactFlowInstance, chatbotId, name, trigger]);

    const handleExportFlow = () => {
        if (reactFlowInstance) {
            const flow = reactFlowInstance.toObject();
            const exportData = {
                name,
                trigger,
                flow
            };

            const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportData, null, 2));
            const downloadAnchorNode = document.createElement('a');
            downloadAnchorNode.setAttribute("href", dataStr);
            downloadAnchorNode.setAttribute("download", `${name.replace(/\s+/g, '_')}_chatbot.json`);
            document.body.appendChild(downloadAnchorNode); // required for firefox
            downloadAnchorNode.click();
            downloadAnchorNode.remove();
        }
    };

    const handleImportFlow = (event) => {
        const fileReader = new FileReader();
        if (event.target.files && event.target.files[0]) {
            fileReader.readAsText(event.target.files[0], "UTF-8");
            fileReader.onload = (e) => {
                try {
                    const parsedData = JSON.parse(e.target.result);

                    if (parsedData.flow) {
                        const { nodes, edges } = parsedData.flow;
                        setNodes(nodes || []);
                        setEdges(edges || []);

                        // Opcional: atualizar nome e gatilho se desejar, ou manter o atual
                        if (parsedData.name) toast.info(`Importado layout de: ${parsedData.name}`);

                        // Forçar ajuste de view se necessário
                        setTimeout(() => reactFlowInstance.fitView(), 100);

                        toast.success("Chatbot importado com sucesso! Não esqueça de Salvar.");
                    } else {
                        toast.error("Arquivo de fluxo inválido");
                    }
                } catch (error) {
                    console.error(error);
                    toast.error("Erro ao ler arquivo");
                }
            };
        }
        // Reset input value to allow re-importing same file
        event.target.value = null;
    };

    return (
        <MainContainer>
            <ConfirmationModal
                title={deletingChatbot && `Delete ${deletingChatbot.name}?`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteChatbot(deletingChatbot.id)}
            >
                Are you sure you want to delete this chatbot?
            </ConfirmationModal>

            {view === "LIST" && (
                <>
                    <MainHeader>
                        <Title>{i18n.t("chatbot.title")}</Title>
                        <MainHeaderButtonsWrapper>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleCreateChatbot}
                            >
                                {i18n.t("chatbot.buttons.add")}
                            </Button>
                        </MainHeaderButtonsWrapper>
                    </MainHeader>
                    <Paper className={classes.mainPaper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">Name</TableCell>
                                    <TableCell align="center">Trigger</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {chatbots.map((bot) => (
                                    <TableRow key={bot.id}>
                                        <TableCell align="center">{bot.name}</TableCell>
                                        <TableCell align="center">{bot.trigger}</TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditChatbot(bot)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setDeletingChatbot(bot);
                                                    setConfirmModalOpen(true);
                                                }}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </>
            )}

            {view === "EDITOR" && (
                <Paper className={classes.editorPaper} variant="outlined">
                    <div className={classes.header}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => setView("LIST")}
                        >
                            Back
                        </Button>
                        <TextField
                            label="Name"
                            variant="outlined"
                            size="small"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />

                        <IconButton
                            onClick={handleExportFlow}
                            title="Exportar Chatbot (Download)"
                            color="primary"
                        >
                            <GetAppIcon />
                        </IconButton>

                        <input
                            accept=".json"
                            style={{ display: 'none' }}
                            id="import-flow-input"
                            type="file"
                            onChange={handleImportFlow}
                        />
                        <label htmlFor="import-flow-input">
                            <IconButton
                                component="span"
                                title="Importar Chatbot (Upload)"
                                color="primary"
                            >
                                <PublishIcon />
                            </IconButton>
                        </label>
                        <TextField
                            label="Trigger"
                            variant="outlined"
                            size="small"
                            value={trigger}
                            onChange={(e) => setTrigger(e.target.value)}
                            placeholder="e.g. hello"
                        />
                        <Button variant="contained" color="primary" onClick={onSave}>
                            Save Flow
                        </Button>
                    </div>

                    <div className={classes.body}>
                        <ReactFlowProvider>
                            <Sidebar />
                            <div className={classes.flowContainer} ref={reactFlowWrapper}>
                                <ReactFlow
                                    nodes={nodes.map(node => ({
                                        ...node,
                                        data: { ...node.data, onDuplicate: () => onDuplicateNode(node.id) }
                                    }))}
                                    edges={edges.map(edge => ({
                                        ...edge,
                                        type: edge.type || 'buttonEdge',
                                        data: { ...edge.data, onDelete: onEdgeDelete }
                                    }))}
                                    onNodesChange={onNodesChange}
                                    onEdgesChange={onEdgesChange}
                                    onEdgeUpdate={onEdgeUpdate}
                                    onEdgeUpdateStart={onEdgeUpdateStart}
                                    onEdgeUpdateEnd={onEdgeUpdateEnd}
                                    onConnect={onConnect}
                                    edgeTypes={edgeTypes}
                                    onInit={setReactFlowInstance}
                                    onDrop={onDrop}
                                    onDragOver={onDragOver}
                                    onNodeClick={onNodeClick}
                                    nodeTypes={nodeTypes}
                                    onNodesDelete={(nodesToDelete) => {
                                        setSelectedNode(null);
                                    }}
                                    deleteKeyCode="Delete"
                                    fitView
                                >
                                    <Controls />
                                    <Background />
                                </ReactFlow>
                            </div>
                            <PropertiesPanel selectedNode={selectedNode} setNodes={setNodes} allNodes={nodes} />
                        </ReactFlowProvider>
                    </div>
                </Paper>
            )}
        </MainContainer>
    );
};

export default Chatbot;
